// Verification result display component
// Frontend developers can modify the visual appearance here

import type {
  VerifyResult,
  ZkProofVerification,
  RawEmailVerification,
} from "../types";

interface VerificationResultProps {
  result: VerifyResult;
  zkProofVerification: ZkProofVerification;
  rawEmailVerification: RawEmailVerification;
  loading: boolean;
  onRefresh: () => void;
}

export function VerificationResult({
  result,
  zkProofVerification,
  rawEmailVerification,
  loading,
  onRefresh,
}: VerificationResultProps) {
  const getResultClass = () => {
    if (result.verification?.contractVerified) {
      return result.verification?.hashMismatch ? "warning" : "authentic";
    }
    if (
      result.hasStoredMetadata &&
      (result.metadata?.proof || result.metadata?.raw_email)
    ) {
      return "embedded-only";
    }
    return "not-found";
  };

  return (
    <div className={`verify-result ${getResultClass()}`}>
      {/* Refresh Button */}
      <RefreshButton loading={loading} onRefresh={onRefresh} />

      {/* Verification Status Banner */}
      <VerificationBanner result={result} />

      {/* Contract Provenance Data */}
      <ContractData result={result} />

      {/* Embedded Signature Data */}
      <EmbeddedSignatureData
        result={result}
        zkProofVerification={zkProofVerification}
        rawEmailVerification={rawEmailVerification}
      />

      {/* Hash Information */}
      <HashInformation
        result={result}
        zkProofVerification={zkProofVerification}
        rawEmailVerification={rawEmailVerification}
      />

      {/* No metadata message */}
      <NoMetadataNote result={result} />

      {/* Not registered message */}
      <NotRegisteredNote
        result={result}
        zkProofVerification={zkProofVerification}
        rawEmailVerification={rawEmailVerification}
      />

      {/* Verification error */}
      {result.verification?.error && (
        <div className="verification-error">
          <p>Could not verify: {result.verification.error}</p>
        </div>
      )}
    </div>
  );
}

// Sub-components

function RefreshButton({
  loading,
  onRefresh,
}: {
  loading: boolean;
  onRefresh: () => void;
}) {
  return (
    <button
      className="refresh-button"
      onClick={onRefresh}
      disabled={loading}
      title="Refresh verification"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M23 4v6h-6" />
        <path d="M1 20v-6h6" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
        <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
      </svg>
      {loading ? "Checking..." : "Refresh"}
    </button>
  );
}

function VerificationBanner({ result }: { result: VerifyResult }) {
  // Hide banner when there's embedded ZK proof but not registered on-chain
  if (
    result.hasStoredMetadata &&
    (result.metadata?.proof || result.metadata?.raw_email) &&
    !result.verification?.contractVerified
  ) {
    return null;
  }

  if (!result.verification?.performed) {
    return null;
  }

  if (result.verification.contractVerified) {
    if (result.verification.hashMismatch) {
      return (
        <div className="verification-banner warning">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>
            Stored Hash Verified On-Chain - Image Content May Have Changed
          </span>
        </div>
      );
    }
    return (
      <div className="verification-banner authentic">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <polyline points="9,12 12,15 16,10" />
        </svg>
        <span>Verified On-Chain - Authentic Image</span>
      </div>
    );
  }

  // Only show "not registered" banner if no embedded signature data
  if (
    !(
      result.hasStoredMetadata &&
      (result.metadata?.proof || result.metadata?.raw_email)
    )
  ) {
    return (
      <div className="verification-banner no-data">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span>No Provenance Data Available</span>
      </div>
    );
  }

  return null;
}

function ContractData({ result }: { result: VerifyResult }) {
  if (
    !result.verification?.contractVerified ||
    !result.verification.contractData
  ) {
    return null;
  }

  return (
    <>
      <div className="verify-header">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22,4 12,14.01 9,11.01" />
        </svg>
        <h3>On-Chain Provenance Data</h3>
      </div>

      <div className="metadata-list">
        <div className="metadata-item">
          <span className="metadata-label">Signed By</span>
          <span className="metadata-value">
            {result.verification.contractData.email}
          </span>
        </div>
        <div className="metadata-item">
          <span className="metadata-label">Signed At</span>
          <span className="metadata-value">
            {new Date(
              result.verification.contractData.timestamp * 1000
            ).toLocaleString()}
          </span>
        </div>
      </div>
    </>
  );
}

function EmbeddedSignatureData({
  result,
  zkProofVerification,
  rawEmailVerification,
}: {
  result: VerifyResult;
  zkProofVerification: ZkProofVerification;
  rawEmailVerification: RawEmailVerification;
}) {
  if (
    !result.hasStoredMetadata ||
    !result.metadata ||
    (!result.metadata.raw_email && !result.metadata.proof)
  ) {
    return null;
  }

  return (
    <div className="hash-section">
      <h4>Embedded Signature Data</h4>
      <div className="metadata-list">
        {/* Raw Email Verification */}
        {result.metadata.raw_email && !result.metadata.proof && (
          <RawEmailSection
            rawEmailVerification={rawEmailVerification}
            result={result}
          />
        )}

        {/* ZK Proof Verification */}
        {result.metadata.proof && (
          <ZkProofSection
            zkProofVerification={zkProofVerification}
            result={result}
          />
        )}
      </div>

      {/* Error notes */}
      {rawEmailVerification.performed &&
        !rawEmailVerification.valid &&
        rawEmailVerification.error && (
          <div className="zk-error-note">
            <p>
              <strong>DKIM Verification Error:</strong>{" "}
              {rawEmailVerification.error}
            </p>
          </div>
        )}

      {rawEmailVerification.performed &&
        rawEmailVerification.valid &&
        rawEmailVerification.hashMatch === false && (
          <div className="zk-hash-mismatch-note">
            <p>
              <strong>Warning:</strong> The signed hash does not match the
              computed hash of this image. This could mean the image content has
              been modified after signing.
            </p>
          </div>
        )}

      {zkProofVerification.performed &&
        !zkProofVerification.valid &&
        zkProofVerification.error && (
          <div className="zk-error-note">
            <p>
              <strong>ZK Verification Error:</strong> {zkProofVerification.error}
            </p>
          </div>
        )}

      {zkProofVerification.performed &&
        zkProofVerification.valid &&
        zkProofVerification.hashMatch === false && (
          <div className="zk-hash-mismatch-note">
            <p>
              <strong>Warning:</strong> The signed hash does not match the
              computed hash of this image. This could mean the image content has
              been modified after signing.
            </p>
          </div>
        )}
    </div>
  );
}

function RawEmailSection({
  rawEmailVerification,
  result,
}: {
  rawEmailVerification: RawEmailVerification;
  result: VerifyResult;
}) {
  return (
    <>
      <div className="metadata-item">
        <span className="metadata-label">Raw Email</span>
        <div className="zk-proof-status">
          <span className="metadata-value embedded-badge">Embedded in image</span>
          {rawEmailVerification.verifying && (
            <span className="zk-verifying">
              <span className="progress-spinner-small"></span>
              Verifying DKIM...
            </span>
          )}
          {rawEmailVerification.performed && (
            <span
              className={`zk-result ${rawEmailVerification.valid ? "valid" : "invalid"}`}
            >
              {rawEmailVerification.valid ? (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="20,6 9,17 4,12" />
                  </svg>
                  DKIM Valid
                </>
              ) : (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                  DKIM Invalid
                </>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Show signer and signed hash when DKIM is valid */}
      {rawEmailVerification.performed && rawEmailVerification.valid && (
        <>
          {rawEmailVerification.emailAddress && (
            <div className="metadata-item">
              <span className="metadata-label">Signed By</span>
              <span className="metadata-value signer-email">
                {rawEmailVerification.emailAddress}
              </span>
            </div>
          )}

          {rawEmailVerification.hashMatch === true &&
            rawEmailVerification.dinoHash && (
              <div className="metadata-item">
                <span className="metadata-label">512-bit Hash</span>
                <code className="metadata-value">
                  {rawEmailVerification.dinoHash}
                </code>
              </div>
            )}

          {rawEmailVerification.hashMatch === false && (
            <>
              <div className="metadata-item">
                <div className="hash-comparison-header">
                  <span className="metadata-label">Signed Hash</span>
                  <span className="match-badge mismatch">
                    Differs from computed
                  </span>
                </div>
                <code className="metadata-value">
                  {rawEmailVerification.dinoHash}
                </code>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Computed Hash</span>
                <code className="metadata-value mismatch">
                  {result.verification?.computed?.dinohash512}
                </code>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

function ZkProofSection({
  zkProofVerification,
  result,
}: {
  zkProofVerification: ZkProofVerification;
  result: VerifyResult;
}) {
  return (
    <>
      <div className="metadata-item">
        <span className="metadata-label">ZK Proof</span>
        <div className="zk-proof-status">
          <span className="metadata-value embedded-badge proof-badge">
            Embedded in image
          </span>
          {zkProofVerification.verifying && (
            <span className="zk-verifying">
              <span className="progress-spinner-small"></span>
              Verifying...
            </span>
          )}
          {zkProofVerification.performed && (
            <span
              className={`zk-result ${zkProofVerification.valid ? "valid" : "invalid"}`}
            >
              {zkProofVerification.valid ? (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="20,6 9,17 4,12" />
                  </svg>
                  Proof Valid
                </>
              ) : (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                  Proof Invalid
                </>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Show signer and signed hash when ZK proof is valid */}
      {zkProofVerification.performed && zkProofVerification.valid && (
        <>
          {zkProofVerification.emailAddress && (
            <div className="metadata-item">
              <span className="metadata-label">Signed By</span>
              <span className="metadata-value signer-email">
                {zkProofVerification.emailAddress}
              </span>
            </div>
          )}

          {zkProofVerification.hashMatch === true &&
            zkProofVerification.dinoHash && (
              <div className="metadata-item">
                <span className="metadata-label">512-bit Hash</span>
                <code className="metadata-value">
                  {zkProofVerification.dinoHash}
                </code>
              </div>
            )}

          {zkProofVerification.hashMatch === false && (
            <>
              <div className="metadata-item">
                <div className="hash-comparison-header">
                  <span className="metadata-label">Signed Hash</span>
                  <span className="match-badge mismatch">
                    Differs from computed
                  </span>
                </div>
                <code className="metadata-value">
                  {zkProofVerification.dinoHash}
                </code>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Computed Hash</span>
                <code className="metadata-value mismatch">
                  {result.verification?.computed?.dinohash512}
                </code>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

function HashInformation({
  result,
  zkProofVerification,
  rawEmailVerification,
}: {
  result: VerifyResult;
  zkProofVerification: ZkProofVerification;
  rawEmailVerification: RawEmailVerification;
}) {
  // Don't show if embedded signature already displays the hash
  if (
    (zkProofVerification.performed && zkProofVerification.valid) ||
    (rawEmailVerification.performed && rawEmailVerification.valid)
  ) {
    return null;
  }

  return (
    <div className="hash-section">
      <h4>Hash Information</h4>
      <div className="metadata-list">
        {/* When hashes match, show single hash */}
        {!result.verification?.hashMismatch && (
          <>
            <div className="metadata-item">
              <span className="metadata-label">512-bit Hash</span>
              <code className="metadata-value">
                {result.verification?.computed?.dinohash512 ||
                  result.metadata?.dinohash512}
              </code>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">96-bit Hash</span>
              <code className="metadata-value">
                {result.verification?.computed?.dinohash96 ||
                  result.metadata?.dinohash96}
              </code>
            </div>
          </>
        )}

        {/* When hashes don't match, show both stored and computed */}
        {result.verification?.hashMismatch && (
          <>
            {result.hasStoredMetadata && result.metadata && (
              <>
                <div className="metadata-item">
                  <div className="hash-comparison-header">
                    <span className="metadata-label">Stored 512-bit Hash</span>
                    <span className="match-badge mismatch">
                      Differs from computed
                    </span>
                  </div>
                  <code className="metadata-value">
                    {result.metadata.dinohash512}
                  </code>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Stored 96-bit Hash</span>
                  <code className="metadata-value">
                    {result.metadata.dinohash96}
                  </code>
                </div>
              </>
            )}

            {result.verification?.computed && (
              <>
                <div className="metadata-item">
                  <span className="metadata-label">Computed 512-bit Hash</span>
                  <code className="metadata-value mismatch">
                    {result.verification.computed.dinohash512}
                  </code>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Computed 96-bit Hash</span>
                  <code className="metadata-value">
                    {result.verification.computed.dinohash96}
                  </code>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Hash mismatch warning */}
      {result.verification?.hashMismatch && (
        <div className="hash-mismatch-note">
          <p>
            <strong>Note:</strong> The computed hash differs from the stored
            hash. This could mean the image content has been modified, but the
            stored hash is still valid according to the on-chain registry.
          </p>
        </div>
      )}
    </div>
  );
}

function NoMetadataNote({ result }: { result: VerifyResult }) {
  if (result.hasStoredMetadata) {
    return null;
  }

  return (
    <div className="no-metadata-note">
      <p>
        <strong>Note:</strong> This image does not have embedded Proteus
        metadata.
        {result.verification?.contractVerified
          ? " However, the computed hash was found in the on-chain registry."
          : " The computed hash was not found in the on-chain registry."}
      </p>
    </div>
  );
}

function NotRegisteredNote({
  result,
  zkProofVerification,
  rawEmailVerification,
}: {
  result: VerifyResult;
  zkProofVerification: ZkProofVerification;
  rawEmailVerification: RawEmailVerification;
}) {
  if (result.verification?.contractVerified) {
    return null;
  }

  return (
    <div className="not-registered-note warning">
      <p>Hash not registered on-chain.</p>
      {((zkProofVerification.performed &&
        zkProofVerification.valid &&
        zkProofVerification.emailAddress) ||
        (rawEmailVerification.performed &&
          rawEmailVerification.valid &&
          rawEmailVerification.emailAddress)) && (
        <p className="signer-warning">
          <strong>Note:</strong> Without on-chain registration, the signer shown
          above may not be the first person who signed this image.
        </p>
      )}
    </div>
  );
}
